/**
 * Sprint 24 — ConnectionStatusPill.
 *
 * Shared status indicator (Q12): a small coloured dot + label, no background.
 * Same vocabulary for MCP and Wrapped connections — "Active" is wrapped-specific
 * phrasing (no OAuth dance), "Connected" the MCP equivalent. Dot colours use
 * semantic tokens so they adapt to light/dark natively.
 */

import type { CSSProperties } from "react";
import type { UnifiedConnectionStatus } from "../../../hooks/use-connections";

const STATUS_STYLE: Record<UnifiedConnectionStatus, { color: string; label: string }> = {
  connected: { color: "var(--color-text-success)", label: "Connected" },
  active: { color: "var(--color-text-success)", label: "Active" },
  "oauth-needed": { color: "var(--color-text-warning)", label: "OAuth needed" },
  "credentials-needed": { color: "var(--color-text-warning)", label: "Credentials needed" },
  error: { color: "var(--color-text-danger)", label: "Error" },
  disabled: { color: "var(--color-text-tertiary)", label: "Disabled" },
};

export interface ConnectionStatusPillProps {
  status: UnifiedConnectionStatus;
}

export function ConnectionStatusPill({ status }: ConnectionStatusPillProps) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.disabled;
  const wrap: CSSProperties = {
    fontSize: 11,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: s.color,
    fontFamily: "var(--font-sans)",
    whiteSpace: "nowrap",
  };
  const dot: CSSProperties = {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: s.color,
    flexShrink: 0,
  };
  return (
    <span style={wrap}>
      <span style={dot} />
      {s.label}
    </span>
  );
}
